// ./src/pages/private/EventTicketsPage/PriceRuleDrawer.tsx
//
// Add / edit one variable-pricing rule on a ticket type. The rule kind decides
// the condition fields:
//   • Early bird (date_window) — an end date (and optional start).
//   • Time of week (time_of_week) — weekdays and/or a time-of-day range.
//   • Membership (membership) — an optional membership type (empty = any member).
// The effect (set price / adjust by amount / adjust by percent) + amount decide
// how the price changes; a Discount/Surcharge toggle signs the adjustment. Rules
// with "stackable" off are exclusive (first match by order wins).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Group, Text, Divider, Chip, SegmentedControl, Box, Input } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { useTranslation } from 'react-i18next';

import { OpResult } from '@sdflc/api-helpers';

import {
  TicketPriceRule,
  TicketPriceRuleInput,
  TicketPriceRuleService,
  TICKET_PRICE_RULE_KIND,
  TICKET_PRICE_EFFECT_KIND,
} from '@/utils/api';
import { useToast, useModalShortcuts, useFormSnapshot, SHORTCUT_HINTS } from '@/hooks';
import { getFormErrors, formatErrors, moveEndWithStart, utcToZonedString, zonedStringToUtc } from '@/utils';
import { AppDrawer, TextField, SelectField, NumberField, SwitchField, DateTimeField, currencyAffixes } from '@/components/generic';
import { MembershipTypeSelect } from '@/components/features';
import { eventZoneNote } from '@/components/features/Events';

const DISCOUNT = 'discount';
const SURCHARGE = 'surcharge';
type Direction = typeof DISCOUNT | typeof SURCHARGE;

// Weekdays, Monday-first for display; values are 0=Sun..6=Sat (as the DB stores).
const WEEKDAYS: { value: string; short: string }[] = [
  { value: '1', short: 'Mon' },
  { value: '2', short: 'Tue' },
  { value: '3', short: 'Wed' },
  { value: '4', short: 'Thu' },
  { value: '5', short: 'Fri' },
  { value: '6', short: 'Sat' },
  { value: '0', short: 'Sun' },
];


// Server error names that map to a visible input: known → inline under the field,
// anything else → generic, in the alert above Save.
const KNOWN_FORM_FIELDS = ['name', 'ruleKind', 'effectKind', 'direction', 'amount', 'isStackable', 'startsAt', 'endsAt', 'daysOfWeek', 'timeFrom', 'timeTo', 'membershipTypeId', 'minQuantity'];

interface RuleForm {
  name: string;
  ruleKind: string;
  effectKind: string;
  direction: Direction;
  amount: number | null; // magnitude the user types (always positive)
  isStackable: boolean;
  startsAt: string | null;
  endsAt: string | null;
  daysOfWeek: string[];
  timeFrom: string;
  timeTo: string;
  membershipTypeId: string | null;
  /** #162 d — "buy more, pay less": the rule applies to every ticket of an order of at least this many. */
  minQuantity: number | null;
}

const emptyForm = (): RuleForm => ({
  name: '',
  ruleKind: TICKET_PRICE_RULE_KIND.DATE_WINDOW,
  effectKind: TICKET_PRICE_EFFECT_KIND.SET_PRICE,
  direction: DISCOUNT,
  amount: null,
  isStackable: false,
  startsAt: null,
  endsAt: null,
  daysOfWeek: [],
  timeFrom: '',
  timeTo: '',
  membershipTypeId: null,
  minQuantity: null,
});

// ⚠ An early-bird window is a wall clock in the EVENT's zone (ROADMAP #93) — loaded and saved there.
const toForm = (r: TicketPriceRule, tz: string | null): RuleForm => {
  const amt = r.amount ?? null;
  const isAdjust = r.effectKind !== TICKET_PRICE_EFFECT_KIND.SET_PRICE;
  return {
    name: r.name ?? '',
    ruleKind: r.ruleKind ?? TICKET_PRICE_RULE_KIND.DATE_WINDOW,
    effectKind: r.effectKind ?? TICKET_PRICE_EFFECT_KIND.SET_PRICE,
    direction: isAdjust && amt != null && amt < 0 ? DISCOUNT : SURCHARGE,
    amount: amt == null ? null : isAdjust ? Math.abs(amt) : amt,
    isStackable: !!r.isStackable,
    startsAt: utcToZonedString(r.startsAt, tz, false),
    endsAt: utcToZonedString(r.endsAt, tz, false),
    daysOfWeek: (r.daysOfWeek ?? []).map((d) => String(d)),
    timeFrom: r.timeFrom ?? '',
    timeTo: r.timeTo ?? '',
    membershipTypeId: r.membershipTypeId ?? null,
    minQuantity: r.minQuantity ?? null,
  };
};

export interface PriceRuleDrawerProps {
  opened: boolean;
  onClose: () => void;
  ticketTypeId: string;
  /** Event currency, shown as a hint on set-price / amount fields. */
  currencyCode?: string | null;
  rule: TicketPriceRule | null;
  /**
   * The event's time zone — an early-bird window and a time-of-week range are both the EVENT's clock (the server prices
   * on it). ⚠ Required.
   */
  timezone: string | null;
  /** order_no to assign a newly-created rule (append at the end). */
  defaultOrderNo: number;
  onSaved: () => void;
}

/**
 * A time-of-day "To" moved with its "From" (same gap), capped at 23:59: a price window runs within ONE day (the server
 * matches FROM ≤ now ≤ TO), so an end that would wrap past midnight stops at the day's end instead.
 */
const timeOfDayAfterMove = (oldFrom: string, oldTo: string, newFrom: string): string => {
  const moved = moveEndWithStart(oldFrom, oldTo, newFrom) ?? oldTo;

  return moved && moved < newFrom ? '23:59' : moved;
};

export const PriceRuleDrawer: React.FC<PriceRuleDrawerProps> = ({
  opened,
  onClose,
  ticketTypeId,
  currencyCode,
  rule,
  timezone,
  defaultOrderNo,
  onSaved,
}) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  // Dates and hours here are the event's — said when this device keeps another zone.
  const zoneNote = eventZoneNote(t, timezone, 'elsewhere');
  const [form, setForm] = useState<RuleForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<OpResult | null>(null);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(submitError, KNOWN_FORM_FIELDS),
    [submitError],
  );
  const nameRef = useRef<HTMLInputElement>(null);

  // Esc / a click outside / Cancel ask before discarding a changed rule (#123).
  const { isDirty, reseed, clear } = useFormSnapshot(form);

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    const seeded = rule ? toForm(rule, timezone) : emptyForm();
    setForm(reseed(seeded, rule?.id ?? '__new__'));
    // ⚠ `timezone` too: the rules drawer does not wait for the event, so a rule opened before its zone arrived is
    // re-read in it — never loaded in this device's zone and saved in the event's. Through `reseed` (#125): a field
    // the user has already changed keeps the change (typed as the event's time, it is saved as one); the rest are re-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, rule, timezone]);

  useEffect(() => {
    if (!opened) return undefined;
    const id = setTimeout(() => nameRef.current?.focus(), 120);
    return () => clearTimeout(id);
  }, [opened]);

  const handleChange = useCallback((name: string | undefined, value: unknown) => {
    if (!name) return;
    setForm((f) => {
      // Moving the start moves the end by the same amount (the window keeps its length); the host adjusts the end.
      if (name === 'startsAt') {
        const v = (value as string | null) ?? null;

        return { ...f, startsAt: v, endsAt: v ? moveEndWithStart(f.startsAt, f.endsAt, v) : f.endsAt };
      }

      return { ...f, [name]: value };
    });
  }, []);

  const isDateWindow = form.ruleKind === TICKET_PRICE_RULE_KIND.DATE_WINDOW;
  const isTimeOfWeek = form.ruleKind === TICKET_PRICE_RULE_KIND.TIME_OF_WEEK;
  const isMembership = form.ruleKind === TICKET_PRICE_RULE_KIND.MEMBERSHIP;
  const isVolume = form.ruleKind === TICKET_PRICE_RULE_KIND.VOLUME;
  const isSetPrice = form.effectKind === TICKET_PRICE_EFFECT_KIND.SET_PRICE;
  const isPercent = form.effectKind === TICKET_PRICE_EFFECT_KIND.ADJUST_PERCENT;

  const ruleKindData = useMemo(
    () => [
      { value: TICKET_PRICE_RULE_KIND.DATE_WINDOW, label: t('events:rules.kindDate', { defaultValue: 'Early bird (date range)' }) },
      { value: TICKET_PRICE_RULE_KIND.TIME_OF_WEEK, label: t('events:rules.kindTime', { defaultValue: 'Time of week' }) },
      { value: TICKET_PRICE_RULE_KIND.MEMBERSHIP, label: t('events:rules.kindMembership', { defaultValue: 'Membership' }) },
      // #162 d (owner, 2026-10-16: "if more than 2 tickets in the order then give discount (percentage/amount) per ticket").
      { value: TICKET_PRICE_RULE_KIND.VOLUME, label: t('events:rules.kindVolume', { defaultValue: 'Buy more, pay less' }) },
    ],
    [t],
  );

  const effectData = useMemo(
    () => [
      { value: TICKET_PRICE_EFFECT_KIND.SET_PRICE, label: t('events:rules.effectSet', { defaultValue: 'Set the price' }) },
      { value: TICKET_PRICE_EFFECT_KIND.ADJUST_PERCENT, label: t('events:rules.effectPercent', { defaultValue: 'Adjust by percent' }) },
      { value: TICKET_PRICE_EFFECT_KIND.ADJUST_AMOUNT, label: t('events:rules.effectAmount', { defaultValue: 'Adjust by amount' }) },
    ],
    [t],
  );

  const windowOrdered = !form.startsAt || !form.endsAt || new Date(form.endsAt) >= new Date(form.startsAt);

  const canSave = useMemo(() => {
    if (saving) return false;
    if (form.amount == null || form.amount <= 0) {
      // set_price allows exactly 0 (a free tier); adjustments need a magnitude.
      if (!(isSetPrice && form.amount === 0)) return false;
    }
    if (isPercent && form.direction === DISCOUNT && (form.amount ?? 0) > 100) return false;
    if (isDateWindow && !form.endsAt) return false;
    if (isVolume && !(form.minQuantity != null && form.minQuantity >= 1)) return false;
    if (isDateWindow && !windowOrdered) return false;
    if (isTimeOfWeek && form.daysOfWeek.length === 0 && !(form.timeFrom && form.timeTo)) return false;
    return true;
  }, [saving, form, isSetPrice, isPercent, isDateWindow, isTimeOfWeek, isVolume, windowOrdered]);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setSubmitError(null);

    const magnitude = form.amount ?? 0;
    const signed = isSetPrice ? magnitude : form.direction === DISCOUNT ? -magnitude : magnitude;

    const input: TicketPriceRuleInput = {
      name: form.name.trim() || null,
      ruleKind: form.ruleKind,
      effectKind: form.effectKind,
      amount: signed,
      isStackable: form.isStackable,
      // Only the condition fields for the chosen kind are kept; the rest are
      // nulled so switching kind never leaves stale conditions behind.
      startsAt: isDateWindow ? zonedStringToUtc(form.startsAt, timezone) : null,
      endsAt: isDateWindow ? zonedStringToUtc(form.endsAt, timezone) : null,
      daysOfWeek: isTimeOfWeek ? form.daysOfWeek.map((d) => Number(d)) : null,
      timeFrom: isTimeOfWeek ? form.timeFrom || null : null,
      timeTo: isTimeOfWeek ? form.timeTo || null : null,
      membershipTypeId: isMembership ? form.membershipTypeId : null,
      minQuantity: isVolume ? form.minQuantity : null,
      maxQuantity: null,
    };

    const result = rule?.id
      ? await TicketPriceRuleService.update(rule.id, input)
      : await TicketPriceRuleService.create({ ...input, ticketTypeId, orderNo: defaultOrderNo });

    setSaving(false);
    if (!result.didSucceed()) {
      // Keep the OpResult: field errors go under their inputs, the rest above Save.
      setSubmitError(result);

      return;
    }
    toast.success(t('events:rules.saved', { defaultValue: 'Pricing rule saved.' }));
    onSaved();
    onClose();
  };

  useModalShortcuts({ onConfirm: save, canConfirm: canSave, enabled: opened });

  const title = rule
    ? t('events:rules.editTitle', { defaultValue: 'Edit pricing rule' })
    : t('events:rules.addTitle', { defaultValue: 'Add pricing rule' });

  const amountLabel = isSetPrice
    ? t('events:rules.setPriceLabel', { defaultValue: 'New price' })
    : isPercent
      ? t('events:rules.percentLabel', { defaultValue: 'Percent' })
      : t('events:rules.amountLabel', { defaultValue: 'Amount' });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      loading={saving}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={save}
      confirmLabel={t('common:buttons.save', { defaultValue: 'Save' })}
      cancelLabel={t('common:buttons.cancel', { defaultValue: 'Cancel' })}
      confirmDisabled={!canSave}
      confirmShortcut={SHORTCUT_HINTS.save}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <TextField
          ref={nameRef}
          name="name"
          label={t('events:rules.name', { defaultValue: 'Name' })}
          description={t('events:rules.nameHint', { defaultValue: 'Optional label, e.g. “Early bird — May”.' })}
          placeholder={t('events:rules.namePh', { defaultValue: 'e.g. Early bird' })}
          value={form.name}
          onChange={handleChange}
          error={fieldErrors.name}
        />

        <SelectField
          name="ruleKind"
          label={t('events:rules.kind', { defaultValue: 'Applies when' })}
          data={ruleKindData}
          value={form.ruleKind}
          onChange={handleChange}
          allowDeselect={false}
          error={fieldErrors.ruleKind}
        />

        {/* ── Condition fields, per kind ── */}
        {isDateWindow && (
          <Group gap="sm" grow align="flex-start">
            <DateTimeField
              name="startsAt"
              label={t('events:rules.startsAt', { defaultValue: 'Starts' })}
              value={form.startsAt}
              onChange={handleChange}
              clearable
              error={fieldErrors.startsAt}
            />
            <DateTimeField
              name="endsAt"
              label={t('events:rules.endsAt', { defaultValue: 'Ends (deadline)' })}
              value={form.endsAt}
              onChange={handleChange}
              notBefore={form.startsAt}
              clearable
              required
              error={
                fieldErrors.endsAt ||
                (!windowOrdered ? t('events:rules.windowError', { defaultValue: 'End must be after the start.' }) : undefined)
              }
            />
          </Group>
        )}

        {isTimeOfWeek && (
          <div>
            <Input.Wrapper label={t('events:rules.days', { defaultValue: 'Days' })} error={fieldErrors.daysOfWeek}>
              <Chip.Group multiple value={form.daysOfWeek} onChange={(v) => setForm((f) => ({ ...f, daysOfWeek: v }))}>
                <Group gap={6}>
                  {WEEKDAYS.map((d) => (
                    <Chip key={d.value} value={d.value} size="md" variant="outline">
                      {t(`events:rules.day.${d.short}`, { defaultValue: d.short })}
                    </Chip>
                  ))}
                </Group>
              </Chip.Group>
            </Input.Wrapper>
            <Group gap="sm" grow align="flex-start" mt="sm">
              <TimeInput
                label={t('events:rules.timeFrom', { defaultValue: 'From' })}
                value={form.timeFrom}
                onChange={(e) => {
                  // Read the value NOW — React strict mode re-invokes the state
                  // updater, and the synthetic event's currentTarget is null by then.
                  const v = e.currentTarget?.value ?? '';
                  // "To" follows "From" by the same amount — but never past midnight: the server matches FROM ≤ now ≤ TO
                  // within one day (no overnight window), so a wrapped "To" would match nothing.
                  setForm((f) => ({ ...f, timeFrom: v, timeTo: v ? timeOfDayAfterMove(f.timeFrom, f.timeTo, v) : f.timeTo }));
                }}
                error={fieldErrors.timeFrom}
              />
              <TimeInput
                label={t('events:rules.timeTo', { defaultValue: 'To' })}
                value={form.timeTo}
                onChange={(e) => {
                  const v = e.currentTarget?.value ?? '';
                  setForm((f) => ({ ...f, timeTo: v }));
                }}
                // ⚠ "To" never before "From" — the picker's floor, and said at the field if typed anyway.
                min={form.timeFrom || undefined}
                error={
                  fieldErrors.timeTo ||
                  (form.timeFrom && form.timeTo && form.timeTo < form.timeFrom
                    ? t('events:rules.timeToBeforeFrom', { defaultValue: 'Must be after “From” — a window cannot run past midnight.' })
                    : undefined)
                }
              />
            </Group>
            <Text size="xs" c="dimmed" mt={4}>
              {t('events:rules.timeHint', { defaultValue: 'Pick weekdays, a time range, or both.' })}
            </Text>
          </div>
        )}

        {(isDateWindow || isTimeOfWeek) && zoneNote && (
          <Text size="md" c="dimmed">
            {zoneNote}
          </Text>
        )}

        {isVolume && (
          <NumberField
            name="minQuantity"
            label={t('events:rules.minQuantity', { defaultValue: 'From this many tickets in one order' })}
            description={t('events:rules.minQuantityHint', {
              defaultValue: 'Every ticket of this type in an order this size or bigger gets the price below — e.g. 3 = "3 or more: 10% off each". Buyers see the offer when they pick tickets.',
            })}
            value={form.minQuantity}
            onChange={handleChange}
            min={1}
            allowDecimal={false}
            allowNegative={false}
            error={fieldErrors.minQuantity}
          />
        )}

        {isMembership && (
          <MembershipTypeSelect
            name="membershipTypeId"
            label={t('events:rules.membershipType', { defaultValue: 'Membership type' })}
            description={t('events:rules.membershipHint', { defaultValue: 'Leave empty to apply to any valid member.' })}
            placeholder={t('events:rules.anyMember', { defaultValue: 'Any member' })}
            value={form.membershipTypeId}
            onChange={handleChange}
            error={fieldErrors.membershipTypeId}
          />
        )}

        <Divider />

        {/* ── Effect ── */}
        <SelectField
          name="effectKind"
          label={t('events:rules.effect', { defaultValue: 'Effect' })}
          data={effectData}
          value={form.effectKind}
          onChange={handleChange}
          allowDeselect={false}
          error={fieldErrors.effectKind}
        />

        {!isSetPrice && (
          // A SegmentedControl takes no `error` of its own — Input.Wrapper carries the
          // server's `direction` refusal, which would otherwise vanish (it is a KNOWN field).
          <Input.Wrapper error={fieldErrors.direction}>
            <SegmentedControl
              fullWidth
              value={form.direction}
              onChange={(v) => setForm((f) => ({ ...f, direction: v as Direction }))}
              data={[
                { value: DISCOUNT, label: t('events:rules.discount', { defaultValue: 'Discount' }) },
                { value: SURCHARGE, label: t('events:rules.surcharge', { defaultValue: 'Surcharge' }) },
              ]}
            />
          </Input.Wrapper>
        )}

        <NumberField
          name="amount"
          label={amountLabel}
          value={form.amount}
          onChange={handleChange}
          min={0}
          max={isPercent && form.direction === DISCOUNT ? 100 : undefined}
          clampBehavior="strict"
          decimalScale={2}
          suffix={isPercent ? '%' : undefined}
          // The Discount/Surcharge control is hidden for "set the price", so a `direction`
          // refusal would have nowhere to land — it signs this amount, so show it here.
          error={fieldErrors.amount || (isSetPrice ? fieldErrors.direction : undefined)}
          // #A4.7b — a fixed amount is in the event's currency: its sign before, its code after (the shared affixes).
          {...(isPercent ? {} : currencyAffixes(currencyCode))}
        />

        <SwitchField
          name="isStackable"
          label={t('events:rules.stackable', { defaultValue: 'Stack with other rules' })}
          description={t('events:rules.stackableHint', {
            defaultValue: 'Off = exclusive (the first matching rule by order wins). On = layers on top of others.',
          })}
          checked={form.isStackable}
          onChange={handleChange}
          error={fieldErrors.isStackable}
        />

        <Box />
      </Stack>
    </AppDrawer>
  );
};
